import { useContext, useId, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CheckBox,
  IconButton,
  Modal,
  TextInput,
  Typography,
  XMarkIcon
} from "@dealer-center/core-components";
import type {
  CheckBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useCreateUserMutation } from "@dealer-center/ddc-pages-configuration-fetch";
import type { UserSummary } from "@dealer-center/ddc-pages-configuration-interface";
import {
  SURFACE_MUTED,
  SURFACE_SUBDUED,
  UserInfoStatusContext,
  validatePassword
} from "../../../components/UserInfo";
import { createErrorMessage } from "../../writeErrorMessages";

export interface AddUserDialogProps {
  accountId: string;
  onClose: () => void;
  onCreated: (user: UserSummary) => void;
}

/** Upstream's limit on the id and both names (Ursula `@Size(1, 50)`). */
export const ADD_USER_MAX_LENGTH = 50;

const TEXT_FIELDS = ["userId", "firstName", "lastName"] as const;

type TextField = (typeof TEXT_FIELDS)[number];

const RULE_KEYS = [
  "PW_RULE_UPPER",
  "PW_RULE_LOWER",
  "PW_RULE_DIGIT",
  "PW_RULE_SPECIAL"
];

const FIELD_IDS: Record<TextField, string> = {
  userId: "usermanager-add-user-id",
  firstName: "usermanager-add-user-first-name",
  lastName: "usermanager-add-user-last-name"
};

const FIELD_LABEL_KEYS: Record<TextField, string> = {
  userId: "USERNAME",
  firstName: "FIRST_NAME",
  lastName: "LAST_NAME"
};

const textFieldError = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return Locale.getText("FIELD_REQUIRED") as string;
  }
  if (trimmed.length > ADD_USER_MAX_LENGTH) {
    return Locale.getText("UM_FIELD_TOO_LONG", {
      max: ADD_USER_MAX_LENGTH
    }) as string;
  }
  return undefined;
};

/**
 * The password lives only in this component's state and the mutation's variables: never a
 * `mutationKey`, query key, analytics property, URL or storage.
 */
export const AddUserDialog = ({
  accountId,
  onClose,
  onCreated
}: AddUserDialogProps) => {
  const createUser = useCreateUserMutation();
  const announce = useContext(UserInfoStatusContext);
  const isCreateInFlightRef = useRef(false);
  const rulesId = useId();

  const [values, setValues] = useState<Record<TextField, string>>({
    userId: "",
    firstName: "",
    lastName: ""
  });
  const [touched, setTouched] = useState<Partial<Record<TextField, boolean>>>(
    {}
  );
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isUsingDealershipInfo, setIsUsingDealershipInfo] = useState(true);
  const [failureMessage, setFailureMessage] = useState<string | undefined>();

  const isSaving = createUser.isPending;
  const passwordResult = validatePassword(password, confirmation);
  const hasTypedPassword = password !== "" || confirmation !== "";
  const isShowingRuleError = hasTypedPassword && !passwordResult.isPolicyMet;
  const isShowingMatchError = hasTypedPassword && passwordResult.isMismatched;
  const errors = TEXT_FIELDS.reduce<Partial<Record<TextField, string>>>(
    (found, field) => {
      const error = textFieldError(values[field]);
      return error ? { ...found, [field]: error } : found;
    },
    {}
  );
  const isValid =
    Object.keys(errors).length === 0 &&
    hasTypedPassword &&
    passwordResult.isValid;

  const handleFieldChange = (field: TextField) => (value: string) => {
    setValues(previous => ({ ...previous, [field]: value }));
  };

  const handleFieldBlur = (field: TextField) => () => {
    setTouched(previous => ({ ...previous, [field]: true }));
  };

  const handleDealershipInfoChange = (
    event: InterstateOnChangeEvent<CheckBoxEventValue>
  ) => {
    setIsUsingDealershipInfo(Boolean(event.target?.value?.checked));
  };

  // Blocked in flight: unmounting drops `mutate`'s callbacks, so the outcome would never be handled.
  const handleClose = () => {
    if (isCreateInFlightRef.current) {
      return;
    }

    onClose();
  };

  const handleSubmit = () => {
    if (isCreateInFlightRef.current || !isValid) {
      return;
    }

    const userId = values.userId.trim();
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();

    isCreateInFlightRef.current = true;
    setFailureMessage(undefined);
    announce("");
    createUser.mutate(
      {
        accountId,
        userId,
        firstName,
        lastName,
        password,
        useDealershipInfo: isUsingDealershipInfo
      },
      {
        onSuccess: () => {
          onCreated({
            userId,
            firstName,
            lastName,
            email: "",
            picture: "",
            lastSignIn: null,
            lastLogin: null
          });
        },
        onError: error => {
          setFailureMessage(createErrorMessage(error));
        },
        onSettled: () => {
          isCreateInFlightRef.current = false;
          // Lets `gcTime: 0` drop the mutation's `variables`, which hold the password.
          createUser.reset();
        }
      }
    );
  };

  const renderTextField = (field: TextField) => {
    const error = touched[field] ? errors[field] : undefined;

    return (
      <TextInput
        required
        data-testid={FIELD_IDS[field]}
        disabled={isSaving}
        errorMessage={error}
        hasError={Boolean(error)}
        id={FIELD_IDS[field]}
        label={Locale.getText(FIELD_LABEL_KEYS[field]) as string}
        name={field}
        value={values[field]}
        onBlur={handleFieldBlur(field)}
        onChange={event => handleFieldChange(field)(event.target.value)}
      />
    );
  };

  return (
    <Modal
      show
      data-testid={"usermanager-add-user-modal"}
      footer={{
        footerComponent: (
          <Box
            sx={{
              borderTop: `1px solid ${SURFACE_MUTED}`,
              columnGap: "8px",
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              width: "100%"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "usermanager-add-user-cancel",
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"usermanager-add-user-cancel"}
              disabled={isSaving}
              onClick={handleClose}
            >
              {Locale.getText("CANCEL") as string}
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: "usermanager-add-user-confirm",
                eventProperties: undefined
              }}
              buttonStyle={"primary"}
              data-testid={"usermanager-add-user-confirm"}
              disabled={isSaving || !isValid}
              onClick={handleSubmit}
            >
              {Locale.getText(isSaving ? "ADDING" : "ADD_USER") as string}
            </Button>
          </Box>
        )
      }}
      header={
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <Typography tag={"h2"} variant={"h5"}>
            {Locale.getText("ADD_USER") as string}
          </Typography>
          {/* `small` renders no close button of its own. */}
          <IconButton
            aria-label={Locale.getText("CLOSE") as string}
            buttonStyle={"tertiary"}
            data-testid={"usermanager-add-user-dismiss"}
            disabled={isSaving}
            size={"sm"}
            onClick={handleClose}
          >
            <XMarkIcon title={Locale.getText("CLOSE") as string} />
          </IconButton>
        </Box>
      }
      size={"small"}
      sx={{ "& .MuiDialog-paper": { maxWidth: "460px", width: "100%" } }}
      onHide={handleClose}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        {renderTextField("userId")}
        {renderTextField("firstName")}
        {renderTextField("lastName")}

        <Box
          data-testid={"usermanager-add-user-password-rules"}
          id={rulesId}
          sx={{
            backgroundColor: SURFACE_SUBDUED,
            borderRadius: "4px",
            padding: "10px 12px"
          }}
        >
          <Typography
            color={"sem.color.on-surface.subdued"}
            tag={"p"}
            variant={"body-sm"}
          >
            {Locale.getText("PW_RULES") as string}
          </Typography>
          <Box component={"ul"} sx={{ marginTop: "6px", paddingLeft: "20px" }}>
            {RULE_KEYS.map(key => (
              <Box component={"li"} key={key}>
                <Typography
                  color={"sem.color.on-surface.subdued"}
                  tag={"span"}
                  variant={"body-sm"}
                >
                  {Locale.getText(key) as string}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          aria-describedby={rulesId}
          role={"group"}
          sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}
        >
          <TextInput
            required
            data-testid={"usermanager-add-user-password"}
            disabled={isSaving}
            id={"usermanager-add-user-password"}
            label={Locale.getText("PASSWORD") as string}
            name={"password"}
            type={"password"}
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
          <TextInput
            required
            data-testid={"usermanager-add-user-password-confirmation"}
            disabled={isSaving}
            id={"usermanager-add-user-password-confirmation"}
            label={Locale.getText("CONFIRM_PASSWORD") as string}
            name={"confirmation"}
            type={"password"}
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
          />

          {isShowingRuleError ? (
            <Alert
              data-testid={"usermanager-add-user-rule-error"}
              role={"alert"}
              type={"error"}
            >
              {Locale.getText("PW_ERR_RULES") as string}
            </Alert>
          ) : null}

          {isShowingMatchError ? (
            <Alert
              data-testid={"usermanager-add-user-match-error"}
              role={"alert"}
              type={"error"}
            >
              {Locale.getText("PASSWORD_MATCH_ERROR") as string}
            </Alert>
          ) : null}
        </Box>

        <CheckBox
          checked={isUsingDealershipInfo}
          data-testid={"usermanager-add-user-dealership-info"}
          disabled={isSaving}
          id={"usermanager-add-user-dealership-info"}
          label={Locale.getText("UM_ADD_USE_DEALERSHIP") as string}
          name={"useDealershipInfo"}
          value={"useDealershipInfo"}
          onChange={handleDealershipInfoChange}
        />

        {failureMessage ? (
          <Alert
            data-testid={"usermanager-add-user-error"}
            role={"alert"}
            type={"error"}
          >
            {failureMessage}
          </Alert>
        ) : null}
      </Box>
    </Modal>
  );
};
